import React, { useEffect, useMemo, useState, useCallback } from "react";
import "../../styles/glass.css";
import "../../styles/globals.css";
import "../../styles/layout.css";
import "../styles/platform.css";
import "../styles/LeftSidebar.css";
import { useCurrentLang } from "../../hooks/useCurrentLang";
import { platformMainPageContent } from "../data/platformMainPageContent";
import HeaderPlatform from "../components/HeaderPlatform";
import FooterPlatform from "../components/FooterPlatform";
import LeftSidebar from "../components/LeftSidebar";
import GlassCard from "../../components/GlassCard";
import BasicInfoBox from "../components/BasicInfoBox";
import NotificationBlock from "../components/NotificationBlock";
import { ExerciseBlock } from "../components/ExerciseBlock";
import TodayPlanCard from "../components/TodayPlanCard";
import BeforeForm from "../components/BeforeForm";
import { useAuth } from "../../auth";
import LoadingPage from "./LoadingPage";
import {
  fetchNotifications,
  deleteNotification,
  markNotificationAsRead,
} from "../api/notificationsApi";
import {getPlan} from "../api/planApi"

export const PlatformMainPage = () => {

  const [expandedId, setExpandedId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const [connectedWithPlan, setConnectedWithPlan] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadPlanStatus() {
      try {
        setLoading(true);
        setError("");

        const plan = await getPlan();

        if (!isMounted) return;

        setConnectedWithPlan(Boolean(plan));
      } catch (err) {
        if (!isMounted) return;

        if (err?.detail === "No active plan found for current user") {
          setConnectedWithPlan(false);
          setError("");
        } else {
          setError(err?.detail || err?.message || "Failed to load plan");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadPlanStatus();

    return () => {
      isMounted = false;
    };
  }, []);


  const currentLang = useCurrentLang();
  const t = platformMainPageContent[currentLang] || platformMainPageContent.en;

  const handleToggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleCompleteExercise = (id) => {
    console.log("Exercise completed:", id);
  };

  async function loadNotifications() {
    try {
      setLoading(true);
      setError("");

      const data = await fetchNotifications();
      setNotifications(data);
    } catch (err) {
      setError(err.message || t.failedLoadNotifications);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id) {
    try {
      setError("");
      await deleteNotification(id);
      setNotifications((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setError(err.message || t.failedDeleteNotification);
    }
  }

  async function handleMarkAsRead(id) {
    try {
      const updated = await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((item) => (item.id === id ? updated : item))
      );
    } catch (err) {
      console.error("Failed to mark notification as read:", err);
    }
  }

  useEffect(() => {
    loadNotifications();
  }, []);

  const mappedNotifications = useMemo(() => {
    return [...notifications]
      .sort((a, b) => {
        const dateA = new Date(a.sent_at || a.created_at || 0).getTime();
        const dateB = new Date(b.sent_at || b.created_at || 0).getTime();
        return dateB - dateA; // newest first
      })
      .slice(0, 5)
      .map((notification) => ({
        id: notification.id,
        title: notification.title || t.notificationFallback,
        subject: notification.title || t.notificationFallback,
        date: notification.sent_at || notification.created_at,
        text: notification.message || "",
        is_read: notification.is_read,
      }));
  }, [notifications, t]);

  if (authLoading) {
    return <LoadingPage />;
  }

  return (
    <div className="revimo-layout">
      <HeaderPlatform />
      <main className="revimo-main">
        <LeftSidebar />
        <div className="revimo-page-content">
          {!connectedWithPlan ? (
            <BeforeForm />
          ) : (
            <>
              <BasicInfoBox />

              <GlassCard
                style={{ marginLeft: "50px", marginTop: "25px", padding: "15px" }}
              >
                <div
                  style={{
                    fontSize: "0.8rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    opacity: 0.7,
                  }}
                >
                  {t.notificationsLabel}
                </div>

                {loading && (
                  <div style={{ marginTop: "16px", opacity: 0.8 }}>
                    {t.loadingNotifications}
                  </div>
                )}

                {error && (
                  <div style={{ marginTop: "16px", color: "#ff6b6b" }}>
                    {error}
                  </div>
                )}

                {!loading && !error && mappedNotifications.length === 0 && (
                  <div style={{ marginTop: "16px", opacity: 0.8 }}>
                    {t.noNotifications}
                  </div>
                )}

                {!loading && !error && mappedNotifications.length > 0 && (
                  <NotificationBlock
                    notifications={mappedNotifications}
                    onDelete={handleDelete}
                    onNotificationClick={handleMarkAsRead}
                    l_m="0px"
                  />
                )}
              </GlassCard>

              <TodayPlanCard />

              
            </>
          )}
        </div>
      </main>
      <footer className="revimo-footer">
        <FooterPlatform />
      </footer>
    </div>
  );
};


export default PlatformMainPage;